/**
 * ¿Esta tabla va a partir GA4 en filas aparte? (tanda «Feedback de Hesta», momento 2)
 *
 * Desde la card eWizKIw6 el Data Node reparte GA4 por campaña: cada fila de Analytics se atribuye
 * a la campaña de Google o Meta que resuelve el vínculo. Pero esa fila sigue siendo de GA4 en todo
 * lo demás, así que con ciertas columnas en la tabla NO cae junto a su campaña:
 *  - Plataforma, Cuenta y Cuenta ID valen ANALYTICS y la cuenta de GA4, no los de la pauta;
 *  - «Estado de campaña» es un dato de la plataforma, y en la fila de GA4 va vacío;
 *  - grupo y anuncio no existen en GA4 (`ownerSourcesOf` en `settings.js` del nodo).
 * El resultado es lo que vio Morten: GA4 en 0 en cada campaña y el total en una fila aparte.
 *
 * Los ids son los del nodo (`settings.js`), que son los del catálogo. Se escribe la lista y no se
 * deduce del grupo del catálogo a propósito: el grupo «Cuenta» trae también el huso horario y la
 * moneda, que en la fila de GA4 pueden coincidir con los de la pauta y no siempre parten nada.
 *
 * Sólo cuenta si la tabla tiene Campaña (o su id): sin campaña no hay reparto que romper, y quitar
 * Plataforma de una tabla «Plataforma × Costo × Ingresos GA4» juntaría todo en un total — el
 * consejo del aviso sería malo justo ahí.
 *
 * **El cruce por grupo y anuncio** (tanda «Cruce paid media ↔ GA4», fase 3) cambió lo de grupo y
 * anuncio: con una regla de etiquetado, el nodo reescribe cada fila de GA4 con la campaña, el grupo
 * y el anuncio de la pauta, y con todo lo demás de esa cuenta (Plataforma, Cuenta, Estado…), así que
 * el GA4 cae en la fila de cada grupo. El nodo lo decide por pedido y esa decisión no llega acá; lo
 * que el editor sí puede saber es si el cliente tiene la regla (`useGa4CrossLevels`). Ver
 * `ga4CrossCovers`.
 */
import {FIELD_KINDS} from './informeModel';

export const GA4_SPLIT_DIMENSIONS = Object.freeze([
    'Canal', 'Cuenta', 'Cuenta_ID', 'Campana_Estado',
    'Grupo_de_Anuncios_ID', 'Grupo_de_Anuncios', 'Grupo_de_Anuncios_Estado',
    'Anuncio_ID', 'Anuncio', 'Anuncio_Estado', 'Anuncio_Link_Preview_Compartible',
    'Anuncio_Link_al_Item_en_Plataforma', 'Anuncio_URL_de_la_Imagen_o_Video', 'Anuncio_Imagen_o_Video',
    'Anuncio_Texto_Principal', 'Anuncio_Cantidad_de_Variantes_de_Texto',
]);

const CAMPAIGN_DIMENSIONS = Object.freeze(['Campana', 'Campana_ID']);

/** Los niveles del cruce, con los nombres de `attributionModel` (`LEVEL_ADGROUP`, `LEVEL_AD`). */
export const CROSS_LEVELS = Object.freeze({ADGROUP: 'adgroup', AD: 'ad'});

// Las columnas que PIDEN un nivel del cruce (`ga4AdsAttributionLevelFields` del nodo). El estado o la
// imagen del anuncio solos no lo piden: sin el anuncio o su id, el nodo no cruza.
const LEVEL_DIMENSIONS = Object.freeze({
    [CROSS_LEVELS.ADGROUP]: Object.freeze(['Grupo_de_Anuncios', 'Grupo_de_Anuncios_ID']),
    [CROSS_LEVELS.AD]: Object.freeze(['Anuncio', 'Anuncio_ID']),
});

// Espejo A MANO de `ga4AdsAttributionColumnFields` (`getData.js` del nodo): las únicas dimensiones con
// las que el nodo cruza. Con cualquier otra (una de GA4, un desglose de Meta, Día de la semana) el
// pedido queda como antes del cruce y GA4 se parte como siempre. Si el nodo suma una, va también acá.
const CROSS_COLUMNS = Object.freeze([
    'Canal', 'Cuenta', 'Cuenta_ID', 'Cuenta_Moneda', 'Huso horario', 'Fecha', 'Semana', 'Mes',
    ...CAMPAIGN_DIMENSIONS, ...GA4_SPLIT_DIMENSIONS,
]);

// Espejo A MANO de `ga4AdsAttributionFilterFields` (`getData.js` del nodo): los únicos filtros del
// informe con los que el nodo cruza. Un filtro sobre otro campo (el grupo, una métrica, una dimensión
// de GA4) da `unsupported_filter`, y el GA4 se parte como siempre. Si el nodo suma uno, va también acá.
const CROSS_FILTER_FIELDS = Object.freeze([
    'Canal', 'Cuenta', 'Cuenta_ID', 'Cuenta_Moneda', 'Huso horario', 'Fecha', 'Semana', 'Mes', ...CAMPAIGN_DIMENSIONS,
]);

// Espejo A MANO de las métricas atómicas de GA4 que en el `settings.js` del nodo no se agregan con SUM
// (AVG o AUTO: tasas, promedios, «por usuario»). Con una en la tabla, el nodo no cruza NADA del pedido
// (`non_additive_ga4_metric`) y también las sesiones se parten. Una fórmula como `GA4__ROAS` sí cruza,
// porque sus ingredientes suman. Si el nodo suma una métrica así, va también acá.
const NON_ADDITIVE_GA4_METRICS = Object.freeze([
    'GA4__Sesiones_por_usuario', 'GA4__Sesiones_comprometidas_por_usuario', 'GA4__Tasa_compromiso', 'GA4__Duración_compromiso_usuario',
    'GA4__Duración_promedio_sesión', 'GA4__Tiempo_compromiso_por_sesión', 'GA4__Tasa_rebote', 'GA4__Vistas_por_usuario',
    'GA4__Vistas_por_sesión', 'GA4__Conteo_eventos_por_usuario', 'GA4__Tasa_conversión_usuario', 'GA4__Tasa_conversión_sesión',
    'GA4__Tasa_compra_a_vista', 'GA4__Tasa_carrito_a_vista', 'GA4__Tasa_checkout_a_compra', 'GA4__Ingresos_promedio_compra',
    'GA4__Valor_promedio_orden', 'GA4__Ingresos_por_usuario', 'GA4__Ingresos_por_sesión', 'GA4__Valor_evento_por_usuario',
    'GA4__Costo_por_clic_anunciante', 'GA4__CTR_anuncio_anunciante', 'GA4__CTR_búsqueda_orgánica_Google', 'GA4__Posición_promedio_búsqueda_orgánica_Google',
    'GA4__Medio_fuente_sesión', 'GA4__Medio_fuente_usuario', 'GA4__Campaña_sesión', 'GA4__Campaña_usuario',
    'GA4__Cantidad_promedio_compra', 'GA4__CTR_anuncio_editor', 'GA4__Sesiones_comprometidas_por_sesión', 'GA4__Tasa_retención_cohorte',
    'GA4__Valor_vida_útil_usuario', 'GA4__Vistas_pantalla_por_usuario', 'GA4__Vistas_pantalla_por_sesión', 'GA4__Ingresos_promedio_por_usuario',
    'GA4__Ingresos_promedio_por_comprador',
]);

// El cruce sólo existe por cuentas publicitarias (el nodo: `dataSourceType === AD_ACCOUNTS`).
const CROSS_SOURCE_TYPE = 'AD_ACCOUNTS';

/** Los `fieldName` de los filtros del informe (formato Looker `[[{fieldName, …}]]`; también una lista plana). */
function filterFieldNames(filters) {
    const names = [];
    (Array.isArray(filters) ? filters : []).forEach((group) => {
        (Array.isArray(group) ? group : [group]).forEach((filter) => {
            if (filter && filter.fieldName) names.push(filter.fieldName);
        });
    });
    return names;
}

// Los grupos del catálogo (`looker_cc/fields_data.py`) de GA4: el estándar y el de los
// personalizados de cada propiedad. Mismo criterio que `fieldKind` de `utils/filterNotes.js`.
const GA4_GROUPS = Object.freeze(['GA4', 'GA4_CUSTOM']);

/** ¿Es una métrica de GA4? Por el grupo del catálogo, o por el prefijo del id si no está. */
export function isGa4Metric(id, fieldsData) {
    const field = fieldsData && fieldsData.FIELD_DATA_BY_ID && fieldsData.FIELD_DATA_BY_ID[id];
    if (field && field.group) return GA4_GROUPS.includes(field.group);
    return /^GA4(C|CM)?__/.test(id || '');
}

/**
 * ¿El cruce por grupo y anuncio junta el GA4 de esta tabla? `dimensions` y `metrics` son los ids de la
 * selección; `cross` = `{sourceType, levels, filters}`, con `levels` los niveles que cruzan TODAS las
 * cuentas de pauta del cliente (`useGa4CrossLevels`) y `filters` los del informe. Pide lo mismo que el
 * nodo (`ga4AdsAttributionShapeOf`): por cuentas, alguna columna de grupo o anuncio, cada nivel pedido
 * con regla, nada más que las columnas y los filtros que el nodo cruza, y ninguna métrica de GA4 que no
 * se sume. Lo que el editor no puede ver queda afuera: la perilla del nodo para la agencia, si alguna
 * GA4 del cliente forma par con una cuenta con regla, y las fórmulas con un ingrediente que no se suma.
 */
export function ga4CrossCovers(dimensions, metrics, cross) {
    if (!cross || cross.sourceType !== CROSS_SOURCE_TYPE) return false;
    const crossed = cross.levels || [];
    const asked = Object.keys(LEVEL_DIMENSIONS)
        .filter((level) => LEVEL_DIMENSIONS[level].some((d) => dimensions.includes(d)));
    if (!asked.length || !asked.every((level) => crossed.includes(level))) return false;
    if (!dimensions.every((d) => CROSS_COLUMNS.includes(d))) return false;
    if (metrics.some((m) => NON_ADDITIVE_GA4_METRICS.includes(m))) return false;
    return filterFieldNames(cross.filters).every((name) => CROSS_FILTER_FIELDS.includes(name));
}

/**
 * Las columnas de la selección que parten GA4, en el orden en que están; `[]` si no hay nada que
 * avisar. `fields` es la lista del modal: `[{name, kind}]`. Con el cruce (`ga4CrossCovers`) no parte
 * ninguna: el nodo copia en la fila de GA4 la Plataforma, la Cuenta y el Estado de la pauta, junto con
 * el grupo y el anuncio. Sin el cruce, todo como antes.
 */
export function ga4SplitColumns(fields, fieldsData, cross = null) {
    const list = fields || [];
    const dimensions = list.filter((f) => f.kind === FIELD_KINDS.DIMENSION).map((f) => f.name);
    const metrics = list.filter((f) => f.kind === FIELD_KINDS.METRIC).map((f) => f.name);
    const hasGa4 = metrics.some((m) => isGa4Metric(m, fieldsData));
    const hasCampaign = dimensions.some((d) => CAMPAIGN_DIMENSIONS.includes(d));
    if (!hasGa4 || !hasCampaign) return [];
    if (ga4CrossCovers(dimensions, metrics, cross)) return [];
    return dimensions.filter((d) => GA4_SPLIT_DIMENSIONS.includes(d));
}

/** La selección sin esas columnas: lo que hace [Quitar esas columnas]. */
export function withoutColumns(fields, ids) {
    const drop = new Set(ids || []);
    return (fields || []).filter((f) => !drop.has(f.name));
}
